import {
  faArrowDownWideShort,
  faBars,
  faCompress,
  faExpand,
  faForward,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faMapPin,
  faPen,
  faStopwatch,
} from "@fortawesome/free-solid-svg-icons";
import {
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  GSButton,
  GSItemList,
  GSProgressBar,
} from "golfstatus_react_components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./tournament-tv-leaderboard-controls.scss";
import { useTheme } from "../../../../hooks/themeHooks";
import PageFooter from "../cart/page-footer";
import { useWakeLock } from "../../../../hooks/pageHooks";
import { useGolfstatusNavigate } from "../../../../hooks/navigationHooks";

const PAGE_SCROLL = "paged";
const CONTINUOUS_SCROLL = "continuous";
const FIXED = "fixed";

const pageTimeoutOptions = [
  { label: "10 sec", timeout: 10000 },
  { label: "8 sec", timeout: 8000 },
  { label: "6 sec", timeout: 6000 },
  { label: "4 sec", timeout: 4000 },
];

const TournamentTVLeaderboardControls = (props) => {
  const { currentPage, currentContent, style, ids } = props;
  const zoomIncrement = 20;

  const [speed, setSpeed] = useState(2);
  const maxSpeed = 5;
  const [pageTimer, setPageTimer] = useState({
    label: "10 sec",
    timeout: 10000,
  });

  const [progress, setProgress] = useState(0);
  const [max, setMax] = useState(0);
  const [gridColumns, setGridColums] = useState({});
  const [scrollType, setScrollType] = useState(FIXED);

  const [zoom, setZoom] = useState(100);

  const interval = useRef(null);

  const [, theme, getStyle] = useTheme();

  const isFullScreen = document.fullscreenElement

  const trackColor = getStyle(theme.secondaryContainer)?.backgroundColor;

  const navigate = useGolfstatusNavigate();

  const { wakeLockSupported, requestWakeLock, releaseWakeLock } = useWakeLock();

  useEffect(() => {
    if (wakeLockSupported) {
      requestWakeLock();
    }
    return () => {
      releaseWakeLock();
    };
  }, [wakeLockSupported, requestWakeLock, releaseWakeLock]);

  useEffect(() => {
    interval.current = setInterval(() => {}, 1000);
    return () => {
      clearInterval(interval.current);
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          /* Firefox */
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          /* Chrome, Safari and Opera */
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          /* IE/Edge */
          document.msExitFullscreen();
        }
      }
    };
  }, []);

  const contrlTimeout = useRef(null);

  const mouseMoving = () => {
    clearTimeout(contrlTimeout?.current);
    setShowControls(true);
    contrlTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 5000);
  };

  const updateSpeed = () => {
    clearInterval(interval.current);
    if (scrollType === CONTINUOUS_SCROLL) {
      updateContinuousSpeed();
    } else {
      updatePageSpeed();
    }
  };

  const updateContinuousSpeed = () => {
    const intervalTimeout = 100;
    let newSpeed = 1;
    if (speed >= maxSpeed) {
      newSpeed = 1;
      setSpeed(newSpeed);
    } else {
      newSpeed = speed + 1;
      setSpeed(newSpeed);
    }
    setScrollInterval(intervalTimeout / newSpeed, scrollType);
  };

  const updatePageSpeed = () => {
    const index = pageTimeoutOptions?.findIndex?.(
      (i) => i.timeout === pageTimer.timeout
    );
    const newIndex = index + 1;
    let update = pageTimeoutOptions[0];
    if (newIndex < pageTimeoutOptions.length) {
      update = pageTimeoutOptions[newIndex];
    }
    setPageTimer(update);
    setScrollInterval(update?.timeout, scrollType);
  };

  const updateScrollType = (update) => {
    clearInterval(interval.current);
    setScrollType(update);
    if (update === PAGE_SCROLL) {
      setScrollInterval(pageTimer.timeout, update);
    } else if (update === CONTINUOUS_SCROLL) {
      setSpeed(2);
      setScrollInterval(100, update);
    }
  };

  const openFullScreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  };
  const closeFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      /* Firefox */
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      /* IE/Edge */
      document.msExitFullscreen();
    }
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      openFullScreen();
    } else {
      closeFullScreen();
    }
  };

  const setScrollInterval = (scrollInterval, type) => {
    interval.current = setInterval(() => {
      if (currentPage) {
        const top = currentPage?.scrollTop;
        const scrollHeight = currentPage?.clientHeight;
        const contentHeight = currentContent?.clientHeight;
        const max = contentHeight - scrollHeight;
        if (Math.ceil(top) < max) {
          if (type === CONTINUOUS_SCROLL) {
            currentPage.scrollTop = currentPage.scrollTop + 1;
          } else {
            currentPage.scrollTo({
              top: currentPage.scrollTop + (scrollHeight - 200),
              behavior: "smooth",
            });
          }
        } else {
          currentPage.scrollTop = 0;
        }
      }
    }, scrollInterval);
  };

  const viewScrolled = useCallback(() => {
    const top = currentPage?.scrollTop;
    setProgress(top);
  }, [currentPage?.scrollTop]);

  useEffect(() => {
    const scrollHeight = currentPage?.clientHeight;
    const contentHeight = currentContent?.clientHeight;
    const maxScroll = contentHeight - scrollHeight;
    setMax(maxScroll);
  }, [currentPage?.clientHeight, currentContent?.clientHeight]);

  useEffect(() => {
    if (currentPage) {
      currentPage?.addEventListener?.("scroll", viewScrolled);
      currentPage?.addEventListener?.("mousemove", mouseMoving);
    }
    return () => {
      currentPage?.removeEventListener?.("scroll", viewScrolled);
      currentPage?.removeEventListener?.("mousemove", mouseMoving);
    };
  }, [currentPage, viewScrolled]);

  const getIdPercentage = useCallback(
    (id) => {
      const el = document.getElementById?.(id);
      const scrollHeight = currentPage?.clientHeight;
      const contentHeight = currentContent?.clientHeight;
      const maxScroll = contentHeight - scrollHeight;
      let percentage = (el?.offsetTop / maxScroll) * 100;
      return percentage;
    },
    [currentPage?.clientHeight, currentContent?.clientHeight]
  );

  const getColumns = useCallback(() => {
    let gridColumns = "";
    let last = 0;
    for (let i = 0; i < ids.length; i++) {
      const percentage = getIdPercentage(ids[i].id);
      if (i > 0) {
        gridColumns += `${percentage - last}% `;
      }
      last = percentage;
    }
    gridColumns += `auto`;
    setGridColums({
      width: "100%",
      maxWidth: "100%",
      gridTemplateColumns: `${gridColumns}`,
    });
  }, [ids, getIdPercentage]);

  useEffect(() => {
    getColumns();
  }, [getColumns]);

  const scrollToLeaderboard = (item) => {
    const lb = document.getElementById(item.id);
    lb.scrollIntoView?.({
      behavior: scrollType === FIXED ? "smooth" : "instant",
      block: "start",
    });
  };

  const trackHeight = 12;

  const chapterBar = () => {
    return (
      <div
        style={{
          ...defaultLayouts.fullWidth,
          ...defaultLayouts.vStack.align("flex-start", "center"),
          position: "absolute",
          overflow: "hidden",
          top: 0,
          left: 0,
        }}
      >
        <GSProgressBar
          value={progress}
          max={max}
          trackStyle={{
            background: trackColor,
            height: trackHeight,
            borderRadius: 0,
          }}
          style={{
            ...getStyle(theme.surfaceVariant),
            width: "100%",
            paddingTop: "0",
            paddingBottom: "0",
            color: "transparent",
            height: trackHeight,
            borderRadius: 0,
          }}
          progressBarStyle={{ color: "transparent", borderRadius: 0 }}
        />
        <GSItemList
          style={{
            ...defaultLayouts.fullWidth,
            position: "absolute",
            top: -2,
            left: 0,
          }}
          type="inline-grid"
          gridColumns={ids?.length}
          items={ids}
          listStyle={gridColumns}
          itemSelected={scrollToLeaderboard}
          listItem={(id) => (
            <div
              title={id.title}
              className="chapter"
              style={{
                ...defaultLayouts.fullWidth,
                height: 24,
                ...getStyle(theme.outlineVariant),
                ...defaultBorders.primaryBorder,
              }}
            ></div>
          )}
        />
      </div>
    );
  };

  const getButtonStyle = (button) => {
    const defaultStyle = { ...defaultBorders.mediumLargeBorderRadius, borderWidth: "2px" };

    if (scrollType === CONTINUOUS_SCROLL && button === "scroll") {
      return {  borderColor: "black", ...defaultStyle };
    }
    if (scrollType === PAGE_SCROLL && button === "page") {
      return {  borderColor: "black", ...defaultStyle };
    }
    if (scrollType === FIXED && button === "fixed") {
      return {  borderColor: "black", ...defaultStyle };
    }
  };

  useEffect(() => {
    if (currentPage?.style?.zoom !== undefined) {
      currentPage.style.zoom = `${zoom}%`;
    }
  }, [zoom, currentPage?.style]);

  const [showControls, setShowControls] = useState(true);

  const zoomIn = () => {
    if(zoom + zoomIncrement < 550){
      setZoom(zoom + zoomIncrement);
    }
    
  };
  const zoomOut = () => {
    if(zoom - zoomIncrement > 0){
      setZoom(zoom - zoomIncrement);
    }
    
  };

  const getPlayer = () => {
    return (
      <tournament-tv-leaderboard-controls style={style}>
        {chapterBar()}
        <div
          style={{
            ...defaultLayouts.hStack.align("center", "center"),
            ...defaultGapping.mediumGap,
            ...defaultPadding.smallLayoutPad.apply("horizontal"),
            ...defaultLayouts.fullWidth,
            flexWrap: "wrap"
          }}
        >
          <GSButton
            buttonIcon={faMapPin}
            title={"fixed"}
            type="mobile-icon"
            onClick={() => {
              updateScrollType(FIXED);
            }}
            style={getButtonStyle("fixed")}
          />
          <GSButton
            buttonIcon={faArrowDownWideShort}
            title={"scroll"}
            type="mobile-icon"
            onClick={() => {
              updateScrollType(CONTINUOUS_SCROLL);
            }}
            style={getButtonStyle("scroll")}
          />
          <GSButton
            buttonIcon={faBars}
            title={"paginate"}
            type="mobile-icon"
            onClick={() => {
              updateScrollType(PAGE_SCROLL);
            }}
            style={getButtonStyle("page")}
          />
          

          {scrollType === CONTINUOUS_SCROLL ? (
            <GSButton
              buttonIcon={faForward}
              title={`${speed}x`}
              style={{ flexShrink: 0 }}
              type="no-wrap grey"
              onClick={updateSpeed}
            />
          ) : null}

          {scrollType === PAGE_SCROLL ? (
            <GSButton
              buttonIcon={faStopwatch}
              title={pageTimer.label}
              style={{ flexShrink: 0 }}
              type="no-wrap grey"
              onClick={updateSpeed}
            />
          ) : null}

          <GSButton
            buttonIcon={faPen}
            title={"edit leaderboards"}
            type="grey mobile-icon"
            onClick={() => {
              navigate("settings");
            }}
          />

          <GSButton
            buttonIcon={faMagnifyingGlassMinus}
            type="grey mobile-icon"
            onClick={zoomOut}
          />
          <GSButton
            buttonIcon={faMagnifyingGlassPlus}
            type="grey mobile-icon"
            onClick={zoomIn}
          />
          <GSButton
            buttonIcon={isFullScreen? faCompress: faExpand}
            title={isFullScreen? "Exit Full Screen": "full screen"}
            type="grey mobile-icon"
            onClick={toggleFullScreen}
          />
        </div>
      </tournament-tv-leaderboard-controls>
    );
  };

  return (
    <PageFooter
      showFooter={true}
      style={{
        transform: showControls
          ? "translateY(0)"
          : "translateY(100%) translateY(-13px)",
        transition: ".4s",
        zIndex: 1,
        position: "fixed",
        bottom: 0,
      }}
    >
      <div onMouseMove={mouseMoving}>{getPlayer()}</div>
    </PageFooter>
  );
};

export default TournamentTVLeaderboardControls;
